import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowPathIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'

const MODULE_LABELS = {
  'payout-safe': 'Payout Safe',
  'compliance-safe': 'Compliance Safe',
  'monitor-safe': 'Monitor Safe',
  'profile-safe': 'Profile Safe',
  'governance-safe': 'Governance Safe',
  executive: 'Executive',
  admin: 'Admin',
}

function statusBadge(status) {
  if (status === 'published') {
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800">Published</span>
  }
  return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">Draft</span>
}

export default function HelpCmsList() {
  const [screens, setScreens] = useState([])
  const [pageFlags, setPageFlags] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newKey, setNewKey] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const [sRes, fRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/admin/help/screens')),
        authFetch(getLocalApiUrl('/api/admin/help/page-flags')),
      ])
      const sJson = await sRes.json()
      const fJson = await fRes.json()
      setScreens(sJson.success ? sJson.data || [] : [])
      setPageFlags(fJson.success ? fJson.data || [] : [])
    } catch {
      setScreens([])
      setPageFlags([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const createFromFlag = async () => {
    const flag = pageFlags.find((f) => f.screen_key === newKey)
    if (!flag) return
    setCreating(true)
    try {
      const enc = encodeURIComponent(flag.screen_key)
      const r = await authFetch(getLocalApiUrl(`/api/admin/help/screens/${enc}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route_path: flag.route_path,
          module: flag.module,
          title: `${flag.name} help`,
          subtitle: '',
          sections: [
            {
              section_key: 'overview',
              title: 'This screen',
              sort_order: 10,
              blocks: [{ type: 'paragraph', text: 'Add help content for this screen.' }],
            },
            {
              section_key: 'legal-footer',
              title: 'Legal footer',
              sort_order: 9999,
              blocks: [{ type: 'legal_footer', text: '' }],
            },
          ],
        }),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Create failed')
      setNewKey('')
      await load()
    } catch (e) {
      alert(e.message || 'Failed to create help screen')
    } finally {
      setCreating(false)
    }
  }

  const grouped = screens.reduce((acc, s) => {
    const mod = s.module || 'other'
    if (!acc[mod]) acc[mod] = []
    acc[mod].push(s)
    return acc
  }, {})

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Help CMS</h1>
          <p className="text-sm text-gray-600 mt-1">
            In-app help for customer screens. Help buttons appear only when content is published.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {pageFlags.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[14rem]">
            <label className="block text-xs font-medium text-gray-600 mb-1">New screen from page flag</label>
            <select
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select page…</option>
              {pageFlags.map((f) => (
                <option key={f.screen_key} value={f.screen_key}>
                  {f.name} ({f.screen_key})
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={!newKey || creating}
            onClick={createFromFlag}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />
            Create
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : screens.length === 0 ? (
        <p className="text-sm text-gray-500">No help screens yet. Create one from a page flag or run the seed script.</p>
      ) : (
        Object.keys(grouped)
          .sort()
          .map((mod) => (
            <div key={mod} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-800">
                {MODULE_LABELS[mod] || mod}
              </div>
              <ul className="divide-y divide-gray-100">
                {grouped[mod].map((s) => (
                  <li key={s.screen_key} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">{s.title}</div>
                      <div className="text-xs text-gray-500 font-mono truncate">{s.screen_key}</div>
                      <div className="text-xs text-gray-400">{s.route_path}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {statusBadge(s.status)}
                      <span className="text-xs text-gray-500">{s.section_count} sections</span>
                      <Link
                        to={`/admin/help/${encodeURIComponent(s.screen_key)}`}
                        className="inline-flex items-center gap-1 text-sm font-medium text-psg-teal hover:underline"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))
      )}
    </div>
  )
}
